import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, Toolbar, AppBar, Select, MenuItem, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import EventsApi from '../../api/eventsApi/eventsApi';
import inspectionReportsApi, { UpsertAnswerPayload } from '../../api/eventsApi/inspectionReportsApi';
import { ReportData, buildReportWorkflowMeta } from '../home/components/reportComponent/reportTypes';
import HtmlReportTemplate from './HtmlReportTemplate';
import ReportLeafletCapture, { ReportLeafletCaptureMode } from './ReportLeafletCapture';
import { generateDocx } from './docxExport';
import './ReportView.css';
import { blobToBase64, getMapImage, getSatelliteMapImage } from '../home/components/reportComponent/reportUtils';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface ChartPayload {
    eventsData: any;
    startDate: string;
    endDate: string;
    selectedEventTypes: string[];
}

const getDefaultChartDateRange = (): { startDate: Date; endDate: Date } => {
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1, 0, 0, 0, 0);

    return { startDate, endDate };
};

const waitForNextPaint = (): Promise<void> =>
    new Promise(resolve => requestAnimationFrame(() => resolve()));

const waitForFontsReady = async (): Promise<void> => {
    const maybeDoc = document as Document & { fonts?: { ready: Promise<unknown> } };
    if (maybeDoc.fonts?.ready) {
        try {
            await maybeDoc.fonts.ready;
        } catch {
            // Ignore font loading failures and continue export.
        }
    }
};

const waitForImagesReady = async (root: HTMLElement): Promise<void> => {
    const images = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
    const pending = images.filter(img => !img.complete || img.naturalHeight === 0);
    if (!pending.length) return;

    await Promise.all(
        pending.map(img => new Promise<void>((resolve) => {
            const done = () => {
                img.removeEventListener('load', done);
                img.removeEventListener('error', done);
                resolve();
            };
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
        }))
    );
};

/** Pin üzerindeki rapor cevaplarını InspectionReport upsert gövdesine çevirir. */
function buildUpsertPayloadFromReportAnswer(
    a: ReportData['questionAnswers'][number]['answer'],
): UpsertAnswerPayload | null {
    const findings = (a.findings || '').trim();
    const proposed = (a.proposedSolutions || '').trim();
    const descRaw = a.description ?? '';
    const desc = descRaw.trim();

    if (typeof a.yesNo === 'boolean') {
        return {
            yesNo: a.yesNo,
            description: descRaw.length ? descRaw : null,
            findings: findings.length ? findings : null,
            proposedSolutions: proposed.length ? proposed : null,
        };
    }

    if (desc === 'Gerek yok') {
        return {
            yesNo: true,
            description: 'Gerek yok',
            findings: findings.length ? findings : null,
            proposedSolutions: proposed.length ? proposed : null,
        };
    }

    if (findings || proposed) {
        return {
            yesNo: false,
            description: descRaw.length ? descRaw : null,
            findings: findings.length ? findings : null,
            proposedSolutions: proposed.length ? proposed : null,
        };
    }

    if (desc) {
        return {
            yesNo: true,
            description: descRaw,
            findings: null,
            proposedSolutions: null,
        };
    }

    return null;
}

const renderPageToCanvas = async (pageElement: HTMLElement): Promise<HTMLCanvasElement> => {
    const exportRoot = document.createElement('div');
    exportRoot.setAttribute('data-export-clone-root', 'true');
    exportRoot.style.position = 'fixed';
    exportRoot.style.left = '-100000px';
    exportRoot.style.top = '0';
    exportRoot.style.pointerEvents = 'none';
    exportRoot.style.opacity = '0';
    exportRoot.style.zIndex = '-1';

    const clone = pageElement.cloneNode(true) as HTMLElement;
    exportRoot.appendChild(clone);
    document.body.appendChild(exportRoot);

    try {
        await waitForFontsReady();
        await waitForImagesReady(clone);
        await waitForNextPaint();

        return await html2canvas(clone, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            imageTimeout: 15000,
        });
    } finally {
        exportRoot.remove();
    }
};

const ReportView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const snapshotReportId = (searchParams.get('reportId') || '').trim() || null;
    const isSnapshot = !!snapshotReportId;
    const [snapshotMeta, setSnapshotMeta] = useState<{
        title: string;
        startedAt?: string | null;
        completedAt?: string | null;
        status?: string | null;
    } | null>(null);
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [userData, setUserData] = useState<{ firstName: string; lastName: string } | null>(null);
    const [attachments, setAttachments] = useState<{ [key: string]: string }>({});
    const [leafletStandardUrl, setLeafletStandardUrl] = useState<string | null>(null);
    const [leafletSatelliteUrl, setLeafletSatelliteUrl] = useState<string | null>(null);
    const [leafletStandardFailed, setLeafletStandardFailed] = useState(false);
    const [leafletSatelliteFailed, setLeafletSatelliteFailed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [chartPayload, setChartPayload] = useState<ChartPayload | null>(null);
    const [exportFormat, setExportFormat] = useState('PDF');
    const [isSaving, setIsSaving] = useState(false);
    const [isPaginationReady, setIsPaginationReady] = useState(false);
    const [attachmentGridColumns, setAttachmentGridColumns] = useState<2 | 3 | 4>(3);
    const [attachmentImageScale, setAttachmentImageScale] = useState<'compact' | 'normal' | 'spacious'>('normal');
    const [mapCaptureRetryKey, setMapCaptureRetryKey] = useState(0);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
    const reportPagesRef = useRef<HTMLDivElement>(null);
    const appBarRef = useRef<HTMLDivElement>(null);
    const leafletCaptureModeRef = useRef<ReportLeafletCaptureMode | null>(null);
    const [stickyToolbarHeight, setStickyToolbarHeight] = useState(0);
    const chartDateRange = useMemo(() => {
        const startDate = chartPayload?.startDate ? new Date(chartPayload.startDate) : undefined;
        const endDate = chartPayload?.endDate ? new Date(chartPayload.endDate) : undefined;
        return { startDate, endDate };
    }, [chartPayload?.startDate, chartPayload?.endDate]);

    useEffect(() => {
        setLeafletStandardUrl(null);
        setLeafletSatelliteUrl(null);
        setLeafletStandardFailed(false);
        setLeafletSatelliteFailed(false);
        setMapCaptureRetryKey(0);
        leafletCaptureModeRef.current = null;
    }, [id]);

    useEffect(() => {
        const toolbarEl = appBarRef.current;
        if (!toolbarEl) return;

        const measure = () => {
            setStickyToolbarHeight(Math.ceil(toolbarEl.getBoundingClientRect().height));
        };

        measure();

        const resizeObserver = new ResizeObserver(() => {
            measure();
        });

        resizeObserver.observe(toolbarEl);
        window.addEventListener('resize', measure);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                setLoading(true);

                const [userInfo, fullReport] = await Promise.all([
                    EventsApi.getUser(),
                    EventsApi.refreshPinFullReport(id),
                ]);
                setUserData({ firstName: userInfo.firstName, lastName: userInfo.lastName });

                let finalReport: ReportData = fullReport;

                if (snapshotReportId) {
                    try {
                        const snapshot = await inspectionReportsApi.getById(snapshotReportId);
                        setSnapshotMeta({
                            title: snapshot.title ?? 'Eski Rapor',
                            startedAt: snapshot.startedAt,
                            completedAt: snapshot.completedAt,
                            status: snapshot.status,
                        });

                        const liveAnswerByQuestionId = new Map<string, ReportData['questionAnswers'][number]['answer']>();
                        for (const qa of fullReport.questionAnswers ?? []) {
                            liveAnswerByQuestionId.set(qa.answer.questionId, qa.answer);
                        }

                        const snapshotQuestionAnswers: ReportData['questionAnswers'] = (snapshot.answers ?? []).map((a) => {
                            const live = liveAnswerByQuestionId.get(a.questionId);
                            return {
                                answer: {
                                    questionId: a.questionId,
                                    questionText: a.questionText ?? live?.questionText ?? '',
                                    yesNo: a.yesNo ?? null,
                                    findings: a.findings ?? '',
                                    proposedSolutions: a.proposedSolutions ?? '',
                                    description: a.description ?? '',
                                    questionCategoryName:
                                        a.questionCategoryName ?? live?.questionCategoryName ?? '',
                                    fileAttachments: (a.fileAttachments ?? []).map((f) => ({
                                        id: f.id,
                                        fileName: f.fileName ?? '',
                                    })),
                                },
                            };
                        });

                        const snapshotRiskAnswers: ReportData['riskAnswers'] = (snapshot.riskAnswers ?? []).map(
                            (ra, idx) => ({
                                id: `snapshot-${snapshotReportId}-${idx}`,
                                probabilityLevel: ra.probabilityLevel,
                                impactLevel: ra.impactLevel,
                                threatLevel: ra.threatLevel,
                                appliedMeasureCoefficient: ra.appliedMeasureCoefficient,
                                residualRiskLevel: ra.residualRiskLevel,
                                isAfterMeasures: ra.isAfterMeasures,
                                categoryName: ra.questionCategoryName ?? '',
                                questionTypeName: '',
                            }),
                        );

                        finalReport = {
                            ...fullReport,
                            questionAnswers: snapshotQuestionAnswers,
                            riskAnswers: snapshotRiskAnswers,
                        };
                    } catch (snapshotError) {
                        console.error('Snapshot rapor yüklenemedi:', snapshotError);
                    }
                } else {
                    setSnapshotMeta(null);
                }

                setReportData(finalReport);

                const newAttachments: { [key: string]: string } = {};
                const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];

                const attachmentIdSeen = new Set<string>();
                const allFiles = [
                    ...(finalReport.pinInfo.fileAttachments || []),
                    ...(finalReport.questionAnswers || []).flatMap((qa: any) => qa.answer.fileAttachments || []),
                ].filter(file => {
                    if (attachmentIdSeen.has(file.id)) return false;
                    attachmentIdSeen.add(file.id);
                    return imageExtensions.some(ext => (file.fileName?.toLowerCase() || '').endsWith(ext));
                });

                await Promise.all(allFiles.map(async file => {
                    try {
                        const blob = await EventsApi.getFileAttachment(file.id);
                        newAttachments[file.id] = await blobToBase64(blob);
                    } catch { /* skip missing files */ }
                }));

                setAttachments(newAttachments);

                // Always fetch last 12 months for report charts.
                let resolvedChartPayload: ChartPayload | null = null;
                try {
                    const { startDate, endDate } = getDefaultChartDateRange();
                    const startDateStr = startDate.toISOString().split('T')[0];
                    const endDateStr = endDate.toISOString().split('T')[0];
                    const eventsData = await EventsApi.getPinEvents(id, startDateStr, endDateStr);

                    resolvedChartPayload = {
                        eventsData,
                        startDate: startDate.toISOString(),
                        endDate: endDate.toISOString(),
                        selectedEventTypes: [],
                    };
                } catch (eventsError) {
                    console.warn('Chart verisi alınamadı, rapor grafikler olmadan render edilecek:', eventsError);
                } finally {
                    // Keep cleanup for older flows that still write localStorage payload.
                    localStorage.removeItem(`report_eventsdata_${id}`);
                }

                setChartPayload(resolvedChartPayload);

            } catch (error) {
                console.error('Error fetching report data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, snapshotReportId]);

    const pinMapCoordsForCapture = useMemo(() => {
        if (!reportData) return null;
        const lat = Number(reportData.pinInfo.Latitude);
        const lng = Number(reportData.pinInfo.Longitude);
        const rawR = Number(reportData.pinInfo.RadiusInMeters);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        const radiusM = !Number.isNaN(rawR) && rawR > 0 ? rawR : 1000;
        return { lat, lng, radiusM };
    }, [reportData]);

    /** ReportLocationPng / harita dosyası referansı (uydu dosyaları hariç). */
    const pinStandardAttachmentId = useMemo(
        () => (reportData ? getMapImage(reportData) : null),
        [reportData],
    );

    /** ReportLocationSatellitePng / harita-uydu dosyası. */
    const pinSatelliteAttachmentId = useMemo(
        () => (reportData ? getSatelliteMapImage(reportData) : null),
        [reportData],
    );

    const standardBlobOk = useMemo(
        () => !!(pinStandardAttachmentId && attachments[pinStandardAttachmentId]),
        [pinStandardAttachmentId, attachments],
    );
    const satelliteBlobOk = useMemo(
        () => !!(pinSatelliteAttachmentId && attachments[pinSatelliteAttachmentId]),
        [pinSatelliteAttachmentId, attachments],
    );

    /** Dosya gerçekten indirilebildiyse veya bu oturumda Leaflet üretildiyse / net başarısızlık. */
    const standardMapResolved =
        standardBlobOk || !!leafletStandardUrl || leafletStandardFailed;
    const satelliteMapResolved =
        satelliteBlobOk || !!leafletSatelliteUrl || leafletSatelliteFailed;

    /** Tek Leaflet ile OSM→uydu sırası bitti veya başarısız sayıldı */
    const offscreenMapCaptureDone =
        !pinMapCoordsForCapture || (standardMapResolved && satelliteMapResolved);

    /** Standart veya uydu eksikse yakalama; ikisi de hazırsa atlanır. */
    const needReportLeafletCapture =
        !loading &&
        !!reportData &&
        !!pinMapCoordsForCapture &&
        (!standardMapResolved || !satelliteMapResolved);

    if (!loading && reportData && leafletCaptureModeRef.current === null) {
        const hasStd = standardBlobOk;
        const hasSat = satelliteBlobOk;
        if (hasStd && !hasSat) {
            leafletCaptureModeRef.current = 'satelliteOnly';
        } else if (!hasStd && hasSat) {
            leafletCaptureModeRef.current = 'standardOnly';
        } else {
            leafletCaptureModeRef.current = 'both';
        }
    }
    const leafletCaptureMode = leafletCaptureModeRef.current ?? 'both';

    const onLeafletStandardCaptured = useCallback((dataUrl: string) => {
        setLeafletStandardUrl(dataUrl);
    }, []);
    const onLeafletSatelliteCaptured = useCallback((dataUrl: string) => {
        setLeafletSatelliteUrl(dataUrl);
    }, []);
    const onLeafletStandardFailed = useCallback(() => {
        setLeafletStandardFailed(true);
    }, []);
    const onLeafletSatelliteFailed = useCallback(() => {
        setLeafletSatelliteFailed(true);
    }, []);

    const handleRetryLeafletMaps = useCallback(() => {
        setLeafletStandardUrl(null);
        setLeafletSatelliteUrl(null);
        setLeafletStandardFailed(false);
        setLeafletSatelliteFailed(false);
        leafletCaptureModeRef.current = null;
        setMapCaptureRetryKey((k) => k + 1);
    }, []);

    /** Upload a PDF snapshot of the rendered report to the pin's attachments */
    const saveReportToBackend = async (): Promise<boolean> => {
        if (!reportData?.pinInfo?.Id || !reportPagesRef.current || !isPaginationReady) return false;
        try {
            setIsSaving(true);

            const pages = Array.from(
                reportPagesRef.current.querySelectorAll<HTMLElement>('[data-export-page="true"]')
            );
            if (!pages.length) {
                return false;
            }

            const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
            const A4_W = 210, A4_H = 297;

            for (let i = 0; i < pages.length; i++) {
                let canvas: HTMLCanvasElement;
                try {
                    canvas = await renderPageToCanvas(pages[i]);
                } catch (captureError: any) {
                    throw new Error(`Sayfa ${i + 1}/${pages.length} render edilemedi: ${captureError?.message || captureError}`);
                }
                const imgData = canvas.toDataURL('image/jpeg', 0.85);
                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, A4_W, A4_H);
            }

            const pdfBlob = pdf.output('blob');
            const d = new Date();
            const datePart = `${String(d.getDate()).padStart(2, '0')}_${String(d.getMonth() + 1).padStart(2, '0')}_${d.getFullYear()}`;
            const safeName = (reportData.pinInfo.Name || 'rapor').replace(/[\\/:\."<>|]+/g, '_');
            // Aynı gün aynı ada ikinci kayıtta çakışmayı önlemek + doğru FileAttachment Id ile complete için benzersiz ad
            const fileName = `${datePart}_${safeName}_raporu_${d.getTime()}.pdf`;

            const formData = new FormData();
            formData.append('Id', reportData.pinInfo.Id);
            formData.append('Files', new File([pdfBlob], fileName, { type: 'application/pdf' }));

            const pinResponse = await EventsApi.updatePin(reportData.pinInfo.Id, formData);

            const list = pinResponse.fileAttachments ?? [];
            const sameName = list.filter((f) => f.fileName === fileName);
            const candidates =
                sameName.length > 0
                    ? sameName
                    : list.filter((f) => (f.fileName || '').toLowerCase().endsWith('.pdf'));
            const sorted = [...candidates].sort((a, b) => {
                const ta = new Date(a.createdDate ?? 0).getTime();
                const tb = new Date(b.createdDate ?? 0).getTime();
                return tb - ta;
            });
            const pdfAttachmentId = sorted[0]?.id;
            if (!pdfAttachmentId) {
                throw new Error('PDF yüklendi ancak dosya kimliği (FileAttachment Id) yanıtta bulunamadı.');
            }

            const user = await EventsApi.getUser();
            const reportTitle = fileName.replace(/\.pdf$/i, '');
            const created = await inspectionReportsApi.create({
                pinId: reportData.pinInfo.Id,
                authorUserId: user.id,
                title: reportTitle,
            });

            await Promise.all(
                reportData.questionAnswers.map(async (qa) => {
                    const qid = qa.answer.questionId;
                    if (!qid) return;
                    const payload = buildUpsertPayloadFromReportAnswer(qa.answer);
                    if (!payload) return;
                    await inspectionReportsApi.upsertAnswer(created.id, qid, payload);
                }),
            );

            await inspectionReportsApi.complete(created.id, { pdfFileAttachmentId: pdfAttachmentId });

            // Signal the source tab (wikipediaModal) to refresh its pdfFiles list
            localStorage.setItem(`report_saved_${reportData.pinInfo.Id}`, Date.now().toString());
            return true;
        } catch (err: any) {
            alert(`HATA: ${err?.message || JSON.stringify(err)}`);
            console.error('[ReportView] Rapor backend kaydı başarısız:', err);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownload = async () => {
        if (!reportData) return;
        if (exportFormat === 'PDF') {
            window.print();
        } else if (exportFormat === 'DOCX') {
            try {
                await generateDocx(reportData, attachments, userData, buildReportWorkflowMeta(reportData, userData));
            } catch (error) {
                console.error('Error generating DOCX:', error);
                alert('DOCX oluşturulurken bir hata oluştu.');
            }
        }
    };

    const handleSaveReportClick = () => {
        if (!reportData) return;
        if (!isPaginationReady) {
            alert('Sayfa düzeni halen hazırlanıyor. Lütfen birkaç saniye sonra tekrar deneyin.');
            return;
        }
        setSaveConfirmOpen(true);
    };

    const handleConfirmSave = async () => {
        setSaveConfirmOpen(false);
        const success = await saveReportToBackend();
        if (success) {
            try {
                if (reportData?.pinInfo?.Id) {
                    await EventsApi.resetAuditData(reportData.pinInfo.Id);
                }
            } catch (e) {
                console.warn('Denetim verileri sıfırlanırken hata:', e);
            }
            alert(
                'Rapor başarıyla kaydedildi! PDF «Kaydedilen Raporlar» listesinde görünecek; soru cevapları da yapılandırılmış denetim raporu olarak saklandı (PDF ile eşleşir).',
            );
        } else {
            alert('Rapor kaydedilemedi. Lütfen tekrar deneyin.');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <CircularProgress />
                <Typography>Rapor hazırlanıyor...</Typography>
            </Box>
        );
    }

    if (!reportData) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography color="error">Rapor bulunamadı veya bir hata oluştu.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#74808a' }}>
            {/* Toolbar – hidden during print */}
            <AppBar
                ref={appBarRef}
                position="sticky"
                className="no-print"
                elevation={2}
                sx={{ backgroundColor: '#0a3d4d' }}
            >
                <Toolbar
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minHeight: { xs: 'auto', md: 56 },
                        py: { xs: 1, md: 0 },
                        gap: 1,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => {
                            try {
                                window.close();
                                setTimeout(() => {
                                    if (!window.closed) navigate('/reports');
                                }, 300);
                            } catch {
                                navigate('/reports');
                            }
                        }}
                        sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' }, flexShrink: 0 }}
                    >
                        Geri
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, justifyContent: 'flex-end', flex: '1 1 auto', minWidth: 0 }}>
                        {isSnapshot && (
                            <Chip
                                icon={<HistoryIcon sx={{ color: '#ffffff !important' }} />}
                                label={
                                    snapshotMeta?.completedAt
                                        ? `Eski Rapor — ${new Date(snapshotMeta.completedAt).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })}`
                                        : snapshotMeta?.startedAt
                                            ? `Eski Rapor — ${new Date(snapshotMeta.startedAt).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })}`
                                            : 'Eski Rapor Önizleme'
                                }
                                title={snapshotMeta?.title || undefined}
                                sx={{
                                    bgcolor: 'rgba(255,193,7,0.18)',
                                    color: '#FFD54F',
                                    fontWeight: 700,
                                    border: '1px solid rgba(255,193,7,0.5)',
                                    '& .MuiChip-icon': { color: '#FFD54F' },
                                    maxWidth: 320,
                                }}
                            />
                        )}
                        {exportFormat === 'PDF' && !isSnapshot && (
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, whiteSpace: 'nowrap' }}>
                                {isPaginationReady
                                    ? 'PDF Kaydet seçeneğini kullanın'
                                    : 'Sayfa düzeni hazırlanıyor...'}
                            </Typography>
                        )}

                        <FormControl size="small" title="Bulgu / öneri / sonuç ek görselleri — yan yana sütun sayısı">
                            <Select
                                value={attachmentGridColumns}
                                onChange={(e) => setAttachmentGridColumns(Number(e.target.value) as 2 | 3 | 4)}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    color: '#ffffff',
                                    minWidth: 112,
                                    borderRadius: 1,
                                    fontSize: { xs: 12, md: 14 },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.8)' },
                                    '& .MuiSvgIcon-root': { color: '#ffffff' },
                                }}
                            >
                                <MenuItem value={2}>Ek görsel: 2 sütun</MenuItem>
                                <MenuItem value={3}>Ek görsel: 3 sütun</MenuItem>
                                <MenuItem value={4}>Ek görsel: 4 sütun</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" title="Ek görsellerin yükseklik ölçeği (PDF ile aynı)">
                            <Select
                                value={attachmentImageScale}
                                onChange={(e) => setAttachmentImageScale(e.target.value as 'compact' | 'normal' | 'spacious')}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    color: '#ffffff',
                                    minWidth: 130,
                                    borderRadius: 1,
                                    fontSize: { xs: 12, md: 14 },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.8)' },
                                    '& .MuiSvgIcon-root': { color: '#ffffff' },
                                }}
                            >
                                <MenuItem value="compact">Görsel: küçük</MenuItem>
                                <MenuItem value="normal">Görsel: orta</MenuItem>
                                <MenuItem value="spacious">Görsel: büyük</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small">
                            <Select
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value)}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    color: '#ffffff',
                                    minWidth: 90,
                                    borderRadius: 1,
                                    fontSize: { xs: 12, md: 14 },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.8)' },
                                    '& .MuiSvgIcon-root': { color: '#ffffff' },
                                }}
                            >
                                <MenuItem value="PDF">PDF</MenuItem>
                                <MenuItem value="DOCX">DOCX</MenuItem>
                            </Select>
                        </FormControl>

                        {!isSnapshot && (
                            <Button
                                variant="contained"
                                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                onClick={handleSaveReportClick}
                                disabled={isSaving || !isPaginationReady}
                                sx={{
                                    backgroundColor: '#4CAF50',
                                    color: '#ffffff',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    fontSize: { xs: 12, md: 14 },
                                    px: { xs: 1.5, md: 2 },
                                    '&:hover': { backgroundColor: '#388E3C' },
                                    '&.Mui-disabled': { backgroundColor: 'rgba(76,175,80,0.5)', color: '#ffffff' },
                                }}
                            >
                                {isSaving ? 'Kaydediliyor...' : 'Raporu Kaydet'}
                            </Button>
                        )}

                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            disabled={isSaving}
                            sx={{
                                backgroundColor: '#ffffff',
                                color: '#0a3d4d',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                fontSize: { xs: 12, md: 14 },
                                px: { xs: 1.5, md: 2 },
                                '&:hover': { backgroundColor: '#e0f0f4', color: '#0a3d4d' },
                                '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.6)', color: '#0a3d4d' },
                            }}
                        >
                            İndir
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Wrapper to allow html2canvas to find .report-page elements for PDF generation */}
            {needReportLeafletCapture && pinMapCoordsForCapture && (
                <ReportLeafletCapture
                    key={`leaflet-cap-${id}-${mapCaptureRetryKey}`}
                    captureMode={leafletCaptureMode}
                    uploadStandardAsReportLocation={!standardBlobOk}
                    uploadSatelliteAsReportLocation={!satelliteBlobOk}
                    pinId={reportData.pinInfo.Id}
                    pinName={reportData.pinInfo.Name || 'pin'}
                    latitude={pinMapCoordsForCapture.lat}
                    longitude={pinMapCoordsForCapture.lng}
                    radiusMeters={pinMapCoordsForCapture.radiusM}
                    onStandardCaptured={onLeafletStandardCaptured}
                    onSatelliteCaptured={onLeafletSatelliteCaptured}
                    onStandardFailed={onLeafletStandardFailed}
                    onSatelliteFailed={onLeafletSatelliteFailed}
                />
            )}

            <div
                ref={reportPagesRef}
                style={{ ['--report-sticky-offset' as string]: `${stickyToolbarHeight}px` }}
            >
                <HtmlReportTemplate
                    reportData={reportData}
                    attachments={attachments}
                    userData={userData}
                    onPaginationReady={setIsPaginationReady}
                    leafletMapStandardUrl={leafletStandardUrl}
                    leafletMapSatelliteUrl={leafletSatelliteUrl}
                    offscreenMapCaptureDone={offscreenMapCaptureDone}
                    onRetryLeafletMaps={handleRetryLeafletMaps}
                    eventsData={chartPayload?.eventsData}
                    chartStartDate={chartDateRange.startDate}
                    chartEndDate={chartDateRange.endDate}
                    selectedEventTypes={chartPayload?.selectedEventTypes}
                    attachmentGridColumns={attachmentGridColumns}
                    attachmentImageScale={attachmentImageScale}
                />
            </div>

            <Dialog open={saveConfirmOpen} onClose={() => setSaveConfirmOpen(false)} maxWidth="xs" fullWidth className="no-print">
                <DialogTitle sx={{ fontWeight: 700 }}>Raporu Kaydet</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        Bu rapor kaydedilecek ve mevcut denetim verileri sıfırlanacaktır. Devam etmek istiyor musunuz?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={() => setSaveConfirmOpen(false)}
                        variant="contained"
                        sx={{
                            fontWeight: 600,
                            backgroundColor: '#e0e0e0 !important',
                            color: '#222 !important',
                            border: '1px solid #bbb',
                            '&:hover': { backgroundColor: '#d0d0d0 !important' },
                            minWidth: 90,
                            boxShadow: 'none',
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        onClick={handleConfirmSave}
                        variant="contained"
                        sx={{
                            fontWeight: 700,
                            minWidth: 90,
                            backgroundColor: '#4CAF50 !important',
                            color: '#fff !important',
                            '&:hover': { backgroundColor: '#388E3C !important' },
                            boxShadow: 'none',
                        }}
                    >
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReportView;
